import { Suspense, useEffect, useLayoutEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthBootstrap } from '@/components/AuthBootstrap';
import { PostSignupRedirect } from '@/components/auth/PostSignupRedirect';
import { RedirectIfAuthenticated } from '@/components/auth/RedirectIfAuthenticated';
import { DevEnvironmentBanner } from '@/components/DevEnvironmentBanner';
import { maybeRefreshRates } from '@/stores/fxStore';
import { useCartSync } from '@/hooks/useCartSync';
import { useStaleCartGuard } from '@/hooks/useStaleCartGuard';
import { ShellLayout } from '@/components/ShellLayout';
import { ToastStack } from '@/components/ToastStack';
import { HomePage } from '@/pages/HomePage';
import { LegacyHubRedirect } from '@/components/LegacyHubRedirect';
import {
  LegacyClassifiedDetailRedirect,
  LegacyPropertyDetailRedirect,
} from '@/components/LegacyMarketplaceRedirects';
import { LegacyBusinessRedirect } from '@/components/seo/LegacyBusinessRedirect';
import { LegacyProductRedirect } from '@/components/seo/LegacyProductRedirect';
import { MARKETPLACE } from '@/lib/marketplacePaths';
import { AccountLegacyRoutes } from '@/routes/AccountLegacyRoutes';
import { hubQueryClient } from '@/lib/hubQueryClient';
import { PlatformConfigProvider } from '@/hooks/usePlatformConfig';
import { SiteSocialCrawlSync } from '@/components/SiteSocialCrawlSync';
import {
  AcceptInvitePage,
  AccountSettingsPage,
  AccountSetupPage,
  AdvertisePage,
  AboutPage,
  BecomeAProPage,
  BoostAdsPage,
  BusinessProfilePage,
  CartPage,
  CheckoutPage,
  ClassifiedDetailPage,
  ClassifiedPostPage,
  ClassifiedsPage,
  ConfirmEmailPage,
  ConnectOnboardPage,
  ConnectProductsPage,
  ConnectStorePage,
  ConnectSuccessPage,
  DealsPage,
  ForgotPasswordPage,
  GiftCardsPage,
  LoginPage,
  LoyaltyPage,
  MessagesPage,
  NotFoundPage,
  NotificationPrefsPage,
  OrderDetailPage,
  OrdersPage,
  OrderTrackingPage,
  PrivacyPage,
  ProDetailPage,
  ProOnboardingPage,
  PropertyDetailPage,
  PropertyPage,
  ReferralPage,
  RegisterPage,
  ResetPasswordPage,
  ReturnsPage,
  SavedCardsPage,
  SearchPage,
  ServiceRequestsPage,
  ShopAssistantPage,
  ShopPage,
  StorePage,
  TermsPage,
  JoinOnboardingPage,
  VendorOnboardingPage,
  VendorPricingPage,
  VendorsPage,
  WishlistPage,
  ProductDetailPage,
  GrenadaHubPage,
  GrenadaCategoryPage,
  ParishCategoryPage,
  MadeInGrenadaPage,
  FindAProGrenadaPage,
  SolarHubPage,
  SolarInstallersPage,
  SolarCompanyPage,
  SolarParishPage,
  SolarProductsPage,
} from '@/routes/lazyPages';
import { lazyDefaultWithRetry } from '@/lib/lazyWithRetry';
import { isHomePath, removeStaticHero } from '@/lib/staticHero';

const HubApp = lazyDefaultWithRetry(() => import('@/routes/HubApp'));

function StaticHeroCleanup() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    if (!isHomePath(pathname)) removeStaticHero();
  }, [pathname]);
  return null;
}

function RouteFallback() {
  return (
    <div
      className="mx-auto max-w-[1400px] animate-pulse px-4 py-16 sm:px-6"
      aria-busy="true"
      aria-label="Loading page"
    >
      <div className="h-8 w-48 rounded-lg bg-slate-200 dark:bg-slate-800" />
      <div className="mt-6 h-40 rounded-xl bg-slate-100 dark:bg-slate-800/80" />
    </div>
  );
}

export default function App() {
  useEffect(() => { maybeRefreshRates(); }, []);
  useCartSync();
  useStaleCartGuard();

  return (
    <QueryClientProvider client={hubQueryClient}>
      <PlatformConfigProvider>
      <SiteSocialCrawlSync />
      <AuthBootstrap />
      <PostSignupRedirect />
      <DevEnvironmentBanner />
      <Suspense fallback={<RouteFallback />}>
        <StaticHeroCleanup />
        <Routes>
          <Route element={<ShellLayout />}>
            <Route path="/" element={<HomePage />} />
            {/* Legacy marketplace hubs (CDN 301 + client-side fallback for in-app navigation) */}
            <Route path="/shop" element={<Navigate to={MARKETPLACE.shop} replace />} />
            <Route path="/vendors" element={<Navigate to={MARKETPLACE.vendors} replace />} />
            <Route path="/businesses" element={<Navigate to={MARKETPLACE.vendors} replace />} />
            <Route path="/classifieds" element={<Navigate to={MARKETPLACE.classifieds} replace />} />
            <Route path="/classifieds/post" element={<Navigate to={MARKETPLACE.classifiedsPost} replace />} />
            <Route path="/classifieds/:id" element={<LegacyClassifiedDetailRedirect />} />
            <Route path="/property" element={<Navigate to={MARKETPLACE.realEstate} replace />} />
            <Route path="/property/:slugOrId" element={<LegacyPropertyDetailRedirect />} />
            <Route path="/find-a-pro" element={<Navigate to={MARKETPLACE.findAPro} replace />} />
            <Route path="/deals" element={<Navigate to={MARKETPLACE.deals} replace />} />
            <Route path="/product/:id" element={<LegacyProductRedirect />} />
            <Route path="/business/:id" element={<LegacyBusinessRedirect />} />
            <Route path="/grenada" element={<GrenadaHubPage />} />
            <Route path="/grenada/solar" element={<SolarHubPage />} />
            <Route path="/grenada/solar/installers" element={<SolarInstallersPage />} />
            <Route path="/grenada/solar/installers/:slug" element={<SolarCompanyPage />} />
            <Route path="/grenada/solar/products" element={<SolarProductsPage />} />
            <Route path="/grenada/solar/sofa-programme" element={<Navigate to="/grenada/solar" replace />} />
            <Route path="/grenada/solar/net-metering" element={<Navigate to="/grenada/solar" replace />} />
            <Route path="/grenada/solar/:parish" element={<SolarParishPage />} />
            {/* Specific Grenada hub pages (must be above the /:category catch-all) */}
            <Route path="/grenada/find-a-pro" element={<FindAProGrenadaPage />} />
            <Route path="/grenada/shop/page/:pageNum" element={<ShopPage />} />
            <Route path="/grenada/shop" element={<ShopPage />} />
            <Route path="/grenada/vendors/page/:pageNum" element={<VendorsPage />} />
            <Route path="/grenada/vendors" element={<VendorsPage />} />
            <Route path="/grenada/find-a-pro/page/:pageNum" element={<FindAProGrenadaPage />} />
            <Route path="/grenada/classifieds/page/:pageNum" element={<ClassifiedsPage />} />
            <Route path="/grenada/real-estate/page/:pageNum" element={<PropertyPage />} />
            <Route path="/grenada/property" element={<Navigate to={MARKETPLACE.realEstate} replace />} />
            <Route path="/grenada/deals/page/:pageNum" element={<DealsPage />} />
            <Route path="/grenada/cars/rental" element={<Navigate to="/grenada/cars" replace />} />
            <Route path="/grenada/cars/for-sale" element={<Navigate to="/grenada/cars" replace />} />
            <Route path="/made-in-grenada" element={<MadeInGrenadaPage />} />
            {/* Detail + post routes (before /grenada/:category/:parish — "post" is not a parish) */}
            <Route path="/grenada/product/:slug" element={<ProductDetailPage />} />
            <Route path="/grenada/business/:businessSlug" element={<BusinessProfilePage />} />
            <Route path="/grenada/property/:slugOrId" element={<PropertyDetailPage />} />
            <Route path="/grenada/deals" element={<DealsPage />} />
            <Route path="/grenada/classifieds/post" element={<ClassifiedPostPage />} />
            <Route path="/grenada/classifieds/:id" element={<ClassifiedDetailPage />} />
            {/* Dependency islands: /grenada/carriacou/{business|product|classifieds|property}/… (before /:category catch-all) */}
            <Route path="/grenada/:parishSlug/business/:businessSlug" element={<BusinessProfilePage />} />
            <Route path="/grenada/:parishSlug/product/:slug" element={<ProductDetailPage />} />
            <Route path="/grenada/:parishSlug/classifieds/:id" element={<ClassifiedDetailPage />} />
            <Route path="/grenada/:parishSlug/property/:slugOrId" element={<PropertyDetailPage />} />
            {/* All other /grenada/:category[/:parish] — param gives GrenadaCategoryPage the category name */}
            <Route path="/grenada/:category/:parish" element={<ParishCategoryPage />} />
            <Route path="/grenada/:category" element={<GrenadaCategoryPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/store/:storeSlug" element={<StorePage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            {/* Account area (Phase 2b) */}
            <Route path="/account" element={<AccountSettingsPage />} />
            <Route path="/account/orders" element={<OrdersPage />} />
            <Route path="/account/orders/:orderId" element={<OrderDetailPage />} />
            <Route path="/account/orders/:orderId/track" element={<OrderTrackingPage />} />
            <Route path="/account/bookings" element={<ServiceRequestsPage />} />
            <Route path="/account/wishlist" element={<WishlistPage />} />
            <Route path="/account/returns" element={<ReturnsPage />} />
            <Route path="/account/messages" element={<MessagesPage />} />
            <Route path="/account/notifications" element={<NotificationPrefsPage />} />
            <Route path="/account/cards" element={<SavedCardsPage />} />
            <Route path="/account/loyalty" element={<LoyaltyPage />} />
            <Route path="/account/referral" element={<ReferralPage />} />
            <Route path="/account/gift-cards" element={<GiftCardsPage />} />
            {AccountLegacyRoutes()}
            <Route path="/pro/:proId" element={<ProDetailPage />} />
            <Route path="/assistant" element={<ShopAssistantPage />} />
            <Route path="/sell" element={<VendorPricingPage />} />
            <Route path="/become-a-pro" element={<BecomeAProPage />} />
            <Route path="/advertise" element={<AdvertisePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/boost" element={<BoostAdsPage />} />
            <Route
              path="/confirm-email"
              element={
                <RedirectIfAuthenticated>
                  <ConfirmEmailPage />
                </RedirectIfAuthenticated>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <RedirectIfAuthenticated>
                  <ForgotPasswordPage />
                </RedirectIfAuthenticated>
              }
            />
            <Route
              path="/reset-password"
              element={
                <RedirectIfAuthenticated>
                  <ResetPasswordPage />
                </RedirectIfAuthenticated>
              }
            />

            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/connect/store" element={<ConnectStorePage />} />
            <Route path="/connect/success" element={<ConnectSuccessPage />} />
            <Route path="/connect/onboard" element={<ConnectOnboardPage />} />
            <Route path="/connect/products" element={<ConnectProductsPage />} />
          </Route>

          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/register"
            element={
              <RedirectIfAuthenticated>
                <RegisterPage />
              </RedirectIfAuthenticated>
            }
          />
          <Route path="/join" element={<JoinOnboardingPage />} />
          <Route path="/vendor/onboard" element={<VendorOnboardingPage />} />
          <Route path="/pro/onboard" element={<ProOnboardingPage />} />
          <Route path="/invite/:token" element={<AcceptInvitePage />} />
          <Route path="/account/setup" element={<AccountSetupPage />} />

          <Route
            path="/hub/*"
            element={
              <Suspense fallback={<RouteFallback />}>
                <HubApp />
              </Suspense>
            }
          />

          <Route path="/vendor/*" element={<LegacyHubRedirect from="vendor" />} />
          <Route path="/pro-hub/*" element={<LegacyHubRedirect from="pro-hub" />} />
          <Route path="/admin/*" element={<LegacyHubRedirect from="admin" />} />

          <Route element={<ShellLayout />}>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
      <ToastStack />
      </PlatformConfigProvider>
    </QueryClientProvider>
  );
}
